/**
 * ESG Impact Assessment Component
 * Comprehensive environmental, social, and governance impact measurement and monitoring interface
 * Follows industry standards for ESG impact assessment and sustainability reporting
 */

import React, { useState, useEffect } from 'react';
import HandshakeIcon from '../../../components/HandshakeIcon';
import {
  subscribe,
  getESGImpactAssessmentOverview,
  getEnvironmentalImpacts,
  getSocialImpacts,
  getGovernanceImpacts,
  getIntegratedMetrics,
  getRiskMatrix,
  getPerformanceMonitoring,
  getHighRiskImpacts,
  calculateImpactTrends,
  getMitigationInvestmentSummary,
  getStakeholderImpactSummary,
  generateImpactAssessmentReport,
  refreshImpactAssessmentData,
  exportImpactAssessmentData
} from '../../../services/esg/esgImpactAssessmentService';

const ESGImpactAssessment = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [impactData, setImpactData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribe((data) => {
      setImpactData(data);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshImpactAssessmentData();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleExport = (format = 'json', category = null) => {
    const filterCriteria = category ? { category } : {};
    const exportData = exportImpactAssessmentData(format, filterCriteria);
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `esg-impact-assessment-${category || 'all'}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(124, 58, 237, 0.3)',
            borderTop: '4px solid #7c3aed',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p style={{ color: 'white', fontSize: '16px', fontWeight: '600' }}>
            Loading ESG Impact Assessment System...
          </p>
        </div>
      </div>
    );
  }

  const overview = getESGImpactAssessmentOverview();
  const integratedMetrics = getIntegratedMetrics();
  const riskMatrix = getRiskMatrix();
  const performanceMonitoring = getPerformanceMonitoring();
  const trends = calculateImpactTrends();
  const investmentSummary = getMitigationInvestmentSummary();
  const stakeholderSummary = getStakeholderImpactSummary();

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
    }}>
      {/* Hero Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #c084fc 100%)',
        padding: '60px 20px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Pattern Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          opacity: 0.1
        }} />

        <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
              <div style={{
                width: '70px',
                height: '70px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '36px',
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'
              }}>
                🎯
              </div>
              <div>
                <h1 style={{
                  fontSize: '42px',
                  fontWeight: '900',
                  color: 'white',
                  margin: '0 0 8px 0',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  letterSpacing: '-0.5px'
                }}>
                  ESG Impact Assessment
                </h1>
                <p style={{
                  fontSize: '16px',
                  color: 'rgba(255, 255, 255, 0.95)',
                  margin: 0,
                  fontWeight: '500'
                }}>
                  Comprehensive environmental, social, and governance impact measurement and monitoring
                </p>
              </div>
            </div>

            <button
              onClick={handleRefresh}
              disabled={refreshing}
              style={{
                padding: '12px 24px',
                background: refreshing ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                opacity: refreshing ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!refreshing) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (!refreshing) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                }
              }}
            >
              {refreshing ? '↻ Refreshing...' : '↻ Refresh Data'}
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', gap: '0', padding: '0 20px' }}>
          {[
            { id: 'overview', name: 'Overview', icon: '📊' },
            { id: 'environmental', name: 'Environmental', icon: '🌱' },
            { id: 'social', name: 'Social', icon: '👥' },
            { id: 'governance', name: 'Governance', icon: '⚖️' },
            { id: 'performance', name: 'Performance', icon: '📈' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: '1',
                padding: '16px 20px',
                background: activeTab === tab.id ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)' : 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid #7c3aed' : '3px solid transparent',
                color: activeTab === tab.id ? 'white' : 'rgba(255, 255, 255, 0.7)',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? '700' : '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.target.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'rgba(255, 255, 255, 0.7)';
                }
              }}
            >
              <span style={{ fontSize: '18px' }}>{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Container */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '40px 20px' }}>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
            {/* Key Metrics */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px'
            }}>
              {/* Card 1: Overall ESG Score */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '16px',
                padding: '24px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(34, 197, 94, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
                  }}>
                    🎯
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '11px',
                      fontWeight: '700',
                      color: '#cbd5e1',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      marginBottom: '4px'
                    }}>
                      Overall ESG Score
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
                  <span style={{
                    fontSize: '48px',
                    fontWeight: '900',
                    color: '#22c55e',
                    textShadow: '0 2px 8px rgba(34, 197, 94, 0.3)',
                    lineHeight: '1'
                  }}>
                    {integratedMetrics.overallESGScore}
                  </span>
                  <span style={{
                    fontSize: '16px',
                    color: '#94a3b8',
                    fontWeight: '600'
                  }}>
                    Score
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: trends.overallTrend.startsWith('+') ? '#22c55e' : '#ef4444'
                  }}>
                    {trends.overallTrend}
                  </span>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>vs last quarter</span>
                </div>
              </div>

              {/* Card 2: Total Impacts */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '16px',
                padding: '24px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(59, 130, 246, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                  }}>
                    📋
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '11px',
                      fontWeight: '700',
                      color: '#cbd5e1',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      marginBottom: '4px'
                    }}>
                      Total Impacts
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
                  <span style={{
                    fontSize: '48px',
                    fontWeight: '900',
                    color: '#60a5fa',
                    textShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                    lineHeight: '1'
                  }}>
                    {overview.totalImpacts}
                  </span>
                  <span style={{
                    fontSize: '16px',
                    color: '#94a3b8',
                    fontWeight: '600'
                  }}>
                    Assessments
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: trends.materialityTrend.startsWith('+') ? '#22c55e' : '#ef4444'
                  }}>
                    {trends.materialityTrend}
                  </span>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>vs last quarter</span>
                </div>
              </div>

              {/* Card 3: High Risk Impacts */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '16px',
                padding: '24px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(239, 68, 68, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
                  }}>
                    ⚠️
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '11px',
                      fontWeight: '700',
                      color: '#cbd5e1',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      marginBottom: '4px'
                    }}>
                      High Risk Impacts
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
                  <span style={{
                    fontSize: '48px',
                    fontWeight: '900',
                    color: '#fbbf24',
                    textShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
                    lineHeight: '1'
                  }}>
                    {overview.highRiskImpacts}
                  </span>
                  <span style={{
                    fontSize: '16px',
                    color: '#94a3b8',
                    fontWeight: '600'
                  }}>
                    Critical
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: '#22c55e'
                  }}>
                    -5.2%
                  </span>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>vs last quarter</span>
                </div>
              </div>

              {/* Card 4: Investment Committed */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                borderRadius: '16px',
                padding: '24px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(168, 85, 247, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, #a855f7 0%, #c084fc 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3)'
                  }}>
                    💰
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '11px',
                      fontWeight: '700',
                      color: '#cbd5e1',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      marginBottom: '4px'
                    }}>
                      Investment Committed
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
                  <span style={{
                    fontSize: '48px',
                    fontWeight: '900',
                    color: '#a855f7',
                    textShadow: '0 2px 8px rgba(168, 85, 247, 0.3)',
                    lineHeight: '1'
                  }}>
                    {Math.round(overview.totalInvestment / 1000000)}
                  </span>
                  <span style={{
                    fontSize: '16px',
                    color: '#94a3b8',
                    fontWeight: '600'
                  }}>
                    M USD
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: '#22c55e'
                  }}>
                    +12.4%
                  </span>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>vs last quarter</span>
                </div>
              </div>
            </div>

            {/* Impact Categories Overview */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Impact Categories Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <CategoryCard
                  title="Environmental"
                  score={integratedMetrics.impactCategories.environmental.score}
                  trend={integratedMetrics.impactCategories.environmental.trend}
                  riskLevel={integratedMetrics.impactCategories.environmental.riskLevel}
                  count={overview.environmentalImpacts}
                  icon="🌍"
                  color="green"
                />
                <CategoryCard
                  title="Social"
                  score={integratedMetrics.impactCategories.social.score}
                  trend={integratedMetrics.impactCategories.social.trend}
                  riskLevel={integratedMetrics.impactCategories.social.riskLevel}
                  count={overview.socialImpacts}
                  icon={<HandshakeIcon size={28} />}
                  color="blue"
                />
                <CategoryCard
                  title="Governance"
                  score={integratedMetrics.impactCategories.governance.score}
                  trend={integratedMetrics.impactCategories.governance.trend}
                  riskLevel={integratedMetrics.impactCategories.governance.riskLevel}
                  count={overview.governanceImpacts}
                  icon="🏛️"
                  color="purple"
                />
              </div>
            </div>

            {/* Stakeholder Value Creation */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Stakeholder Value Creation</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {Object.entries(overview.stakeholderValue).map(([stakeholder, data]) => (
                  <div key={stakeholder} className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-lg font-semibold text-gray-900 capitalize">{stakeholder}</div>
                    <div className="text-2xl font-bold text-green-600">{data.value > 0 ? '+' : ''}{data.value.toFixed(1)}</div>
                    <div className="text-sm text-gray-600">{data.unit}</div>
                    {data.satisfaction && (
                      <div className="text-sm text-gray-500 mt-1">{data.satisfaction}% satisfied</div>
                    )}
                    {data.improvement && (
                      <div className="text-sm text-green-600 mt-1">{data.improvement}% improved</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* SDG Alignment */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">UN SDGs Alignment</h2>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {Object.entries(integratedMetrics.sdgAlignment).map(([sdg, data]) => (
                  <div key={sdg} className="text-center p-4 border rounded-lg">
                    <div className="text-lg font-semibold text-gray-900">{sdg.toUpperCase()}</div>
                    <div className="text-sm text-gray-600 mb-2">{data.contribution} Impact</div>
                    <div className="text-xl font-bold text-blue-600">{data.score}</div>
                    <div className="text-sm text-gray-500">{data.initiatives} initiatives</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'environmental' && (
          <EnvironmentalImpactsView impactData={getEnvironmentalImpacts()} onExport={() => handleExport('json', 'environmental')} />
        )}

        {activeTab === 'social' && (
          <SocialImpactsView impactData={getSocialImpacts()} onExport={() => handleExport('json', 'social')} />
        )}

        {activeTab === 'governance' && (
          <GovernanceImpactsView impactData={getGovernanceImpacts()} onExport={() => handleExport('json', 'governance')} />
        )}

        {activeTab === 'performance' && (
          <PerformanceView 
            performanceData={performanceMonitoring}
            riskMatrix={riskMatrix}
            investmentSummary={investmentSummary}
            stakeholderSummary={stakeholderSummary}
          />
        )}
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, unit, trend, icon, color }) => {
  const colorClasses = {
    green: 'bg-green-50 border-green-200',
    blue: 'bg-blue-50 border-blue-200',
    red: 'bg-red-50 border-red-200',
    purple: 'bg-purple-50 border-purple-200'
  };

  const trendColor = trend.startsWith('+') ? 'text-green-600' : trend.startsWith('-') ? 'text-red-600' : 'text-gray-600';

  return (
    <div className={`bg-white rounded-xl shadow-lg p-6 border-l-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{icon}</span>
        <span className={`text-sm font-medium ${trendColor}`}>{trend}</span>
      </div>
      <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
      <div className="flex items-baseline">
        <span className="text-2xl font-bold text-gray-900">{value}</span>
        <span className="text-sm text-gray-500 ml-1">{unit}</span>
      </div>
    </div>
  );
};

// Category Card Component
const CategoryCard = ({ title, score, trend, riskLevel, count, icon, color }) => {
  const colorClasses = {
    green: 'border-green-200 bg-green-50',
    blue: 'border-blue-200 bg-blue-50',
    purple: 'border-purple-200 bg-purple-50'
  };

  const riskColors = {
    Low: 'text-green-600 bg-green-100',
    Medium: 'text-yellow-600 bg-yellow-100',
    High: 'text-red-600 bg-red-100'
  };

  return (
    <div className={`border-2 rounded-lg p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-2xl">{icon}</span>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${riskColors[riskLevel]}`}>
          {riskLevel} Risk
        </span>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <div className="flex items-baseline justify-between">
        <div>
          <span className="text-xl font-bold text-gray-900">{score.toFixed(1)}</span>
          <span className="text-sm text-gray-500 ml-1">Score</span>
        </div>
        <div className="text-right">
          <div className="text-sm text-green-600 font-medium">{trend}</div>
          <div className="text-xs text-gray-500">{count} assessments</div>
        </div>
      </div>
    </div>
  );
};

// Environmental Impacts View
const EnvironmentalImpactsView = ({ impactData, onExport }) => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <h2 className="text-2xl font-bold text-gray-900">Environmental Impact Assessments</h2>
      <button onClick={onExport} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
        Export Environmental Data
      </button>
    </div>
    
    <div className="grid gap-6">
      {impactData.map((impact) => (
        <ImpactCard key={impact.id} impact={impact} category="environmental" />
      ))}
    </div>
  </div>
);

// Social Impacts View
const SocialImpactsView = ({ impactData, onExport }) => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <h2 className="text-2xl font-bold text-gray-900">Social Impact Assessments</h2>
      <button onClick={onExport} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
        Export Social Data
      </button>
    </div>
    
    <div className="grid gap-6">
      {impactData.map((impact) => (
        <ImpactCard key={impact.id} impact={impact} category="social" />
      ))}
    </div>
  </div>
);

// Governance Impacts View
const GovernanceImpactsView = ({ impactData, onExport }) => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <h2 className="text-2xl font-bold text-gray-900">Governance Impact Assessments</h2>
      <button onClick={onExport} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
        Export Governance Data
      </button>
    </div>
    
    <div className="grid gap-6">
      {impactData.map((impact) => (
        <ImpactCard key={impact.id} impact={impact} category="governance" />
      ))}
    </div>
  </div>
);

// Impact Card Component
const ImpactCard = ({ impact, category }) => {
  const [expanded, setExpanded] = useState(false);
  
  const severityColors = {
    High: 'text-red-600 bg-red-100',
    Medium: 'text-yellow-600 bg-yellow-100',
    Low: 'text-green-600 bg-green-100'
  };

  const impactTypeColors = {
    Negative: 'text-red-600',
    Positive: 'text-green-600',
    Mixed: 'text-yellow-600'
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">{impact.category}</h3>
              <span className="text-sm text-gray-500">|</span>
              <span className="text-sm text-gray-600">{impact.subcategory}</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${severityColors[impact.severity]}`}>
                {impact.severity} Impact
              </span>
            </div>
            <p className="text-gray-600 mb-2">{impact.scope}</p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Materiality:</span>
                <span className="font-semibold text-gray-900">{impact.materialityScore.toFixed(1)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Type:</span>
                <span className={`font-medium ${impactTypeColors[impact.impactType]}`}>
                  {impact.impactType}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Likelihood:</span>
                <span className="font-semibold text-gray-900">{impact.likelihood}%</span>
              </div>
            </div>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="ml-4 p-2 text-gray-400 hover:text-gray-600"
          >
            {expanded ? '−' : '+'}
          </button>
        </div>

        {/* Key Metrics Preview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          {Object.entries(impact.metrics).slice(0, 4).map(([key, metric]) => (
            <div key={key} className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</div>
              <div className="font-semibold text-gray-900">{metric.value} {metric.unit}</div>
              <div className={`text-xs ${metric.trend.startsWith('+') ? 'text-green-600' : metric.trend.startsWith('-') ? 'text-red-600' : 'text-gray-600'}`}>
                {metric.trend}
              </div>
            </div>
          ))}
        </div>

        {expanded && (
          <div className="space-y-6 border-t pt-6">
            {/* All Metrics */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">All Metrics</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(impact.metrics).map(([key, metric]) => (
                  <div key={key} className="p-3 border rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</div>
                    <div className="font-semibold text-gray-900">{metric.value} {metric.unit}</div>
                    <div className="flex justify-between text-xs mt-1">
                      <span className={metric.trend.startsWith('+') ? 'text-green-600' : metric.trend.startsWith('-') ? 'text-red-600' : 'text-gray-600'}>
                        {metric.trend}
                      </span>
                      <span className="text-gray-500">Target: {metric.target}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mitigation Measures */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">Mitigation Measures</h4>
              <div className="grid gap-3">
                {impact.mitigationMeasures.map((measure, index) => (
                  <div key={index} className="p-4 border rounded-lg bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <h5 className="font-medium text-gray-900">{measure.measure}</h5>
                      <span className="text-sm text-gray-500">{measure.timeline}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">Investment: </span>
                        <span className="font-medium">${(measure.investment / 1000000).toFixed(1)}M</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Effectiveness: </span>
                        <span className="font-medium text-green-600">{measure.effectiveness.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stakeholder Impact */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">Stakeholder Impact</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(impact.stakeholderImpact).map(([stakeholder, data]) => (
                  <div key={stakeholder} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="font-medium text-gray-900 capitalize">{stakeholder}</h5>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${severityColors[data.severity]}`}>
                        {data.severity}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{data.nature}</p>
                    <p className="text-xs text-gray-500">Affected: {data.affected}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Compliance Frameworks */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">Compliance Frameworks</h4>
              <div className="flex flex-wrap gap-2">
                {impact.complianceFrameworks.map((framework, index) => (
                  <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                    {framework}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Performance View
const PerformanceView = ({ performanceData, riskMatrix, investmentSummary, stakeholderSummary }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-gray-900">Performance Monitoring</h2>
    
    {/* KPI Dashboard */}
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Performance Indicators</h3>
      <div className="grid gap-4">
        {performanceData.map((kpi, index) => (
          <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <h4 className="font-medium text-gray-900">{kpi.name}</h4>
              <div className="flex items-center gap-4 mt-1">
                <span className="text-sm text-gray-600">Current: {kpi.current} {kpi.unit}</span>
                <span className="text-sm text-gray-600">Target: {kpi.target} {kpi.unit}</span>
                <span className={`text-sm ${kpi.trend.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                  {kpi.trend}
                </span>
              </div>
            </div>
            <div className="text-right">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                kpi.status === 'Exceeding' ? 'bg-green-100 text-green-800' :
                kpi.status === 'On Track' ? 'bg-blue-100 text-blue-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {kpi.status}
              </span>
              <div className="text-xs text-gray-500 mt-1">{kpi.frequency}</div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Risk Matrix */}
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Assessment Matrix</h3>
      <div className="grid gap-4">
        {riskMatrix.map((risk, index) => (
          <div key={index} className="p-4 border rounded-lg">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h4 className="font-medium text-gray-900">{risk.category}</h4>
                <p className="text-sm text-gray-600">{risk.subcategories.join(', ')}</p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-red-600">{risk.riskScore.toFixed(1)}</div>
                <div className="text-xs text-gray-500">Risk Score</div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Likelihood: </span>
                <span className="font-medium">{risk.likelihood}</span>
              </div>
              <div>
                <span className="text-gray-600">Impact: </span>
                <span className="font-medium">{risk.impact}</span>
              </div>
              <div>
                <span className="text-gray-600">Horizon: </span>
                <span className="font-medium">{risk.timeHorizon}</span>
              </div>
              <div>
                <span className="text-gray-600">Status: </span>
                <span className="font-medium text-blue-600">{risk.mitigationStatus}</span>
              </div>
            </div>
            <div className="mt-2 text-sm">
              <span className="text-gray-600">Financial Impact: </span>
              <span className="font-medium">${(risk.financialImpact.min / 1000000).toFixed(0)}M - ${(risk.financialImpact.max / 1000000).toFixed(0)}M {risk.financialImpact.currency}</span>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Investment Summary */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Investment Summary</h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Total Investment</span>
            <span className="font-bold text-green-600">${(investmentSummary.totalInvestment / 1000000).toFixed(1)}M</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Average Effectiveness</span>
            <span className="font-bold text-blue-600">{investmentSummary.averageEffectiveness.toFixed(1)}%</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Total Measures</span>
            <span className="font-bold text-gray-900">{investmentSummary.measureCount}</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Stakeholder Impact Summary</h3>
        <div className="space-y-2">
          {Object.entries(stakeholderSummary).map(([stakeholder, data]) => (
            <div key={stakeholder} className="flex justify-between items-center">
              <span className="text-gray-600 capitalize">{stakeholder}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{data.impacts} impacts</span>
                <div className="flex gap-1">
                  {data.severityHigh > 0 && <span className="w-2 h-2 bg-red-500 rounded-full" title="High severity"></span>}
                  {data.severityMedium > 0 && <span className="w-2 h-2 bg-yellow-500 rounded-full" title="Medium severity"></span>}
                  {data.severityLow > 0 && <span className="w-2 h-2 bg-green-500 rounded-full" title="Low severity"></span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default ESGImpactAssessment;